import isNull from 'lodash-es/isNull';
import isObject from 'lodash-es/isObject';
import isUndefined from 'lodash-es/isUndefined';
import cloneJSON, { JsonValue } from './cloneJSON';
import { LOG } from './log';

type FieldOpt = {
  /** 字段值名称 */
  name: string;
  /** 是否是主键 */
  isPrimary?: boolean;
  /** 是否唯一 */
  unique?: boolean;
};

export default class InexDB {
  IDB: IDBOpenDBRequest | null = null;

  DB: IDBDatabase | null = null;

  fieldOpts: FieldOpt[] = [];

  storeName: string = '';

  constructor(storeName: string, fieldOpts: FieldOpt[]) {
    this.storeName = storeName;
    this.fieldOpts = fieldOpts;
  }

  // eslint-disable-next-line max-len
  openDB = () => new Promise<{ DB: IDBDatabase | null; IDB: IDBOpenDBRequest | null }>((resolve) => {
    LOG?.set({
      logType: 'indexDB-open-start',
    });
    let IDB: any = {};

    try {
      IDB = indexedDB.open(this.storeName, 2);
    } catch {
      resolve({ IDB, DB: null });
    }

    IDB.onerror = () => {
      resolve({
        IDB,
        DB: null,
      });
    };

    IDB.onsuccess = (event: any) => {
      const db = event.target.result;
      LOG?.set({
        logType: 'indexDB-open-success',
      });
      resolve({
        IDB,
        DB: db,
      });
    };

    IDB.onupgradeneeded = (event: any) => {
      const db = event.target.result;

      const mainKey = this.fieldOpts.find((fieldOpt) => fieldOpt.isPrimary)?.name;

      if (mainKey) {
        // 建立一个对象仓库来存储页面数据的相关信息
        const objectStore = db.createObjectStore(this.storeName, { keyPath: mainKey });

        this.fieldOpts
          .filter((fieldOpt) => fieldOpt.name !== mainKey)
          .forEach((fieldOpt) => {
            const { name, unique = false } = fieldOpt;

            objectStore.createIndex(name, name, { unique });
          });
      }
    };
  });

  // eslint-disable-next-line max-len, no-async-promise-executor
  save = (key: string, data: JsonValue) => new Promise<boolean>((resolve) => {
    data = cloneJSON(data);

    (async () => {
      if (isUndefined(this.IDB) || isNull(this.IDB) || this.IDB.readyState !== 'done') {
        const { IDB, DB } = await this.openDB();

        this.IDB = IDB;
        this.DB = DB;

        if (!this.DB) return resolve(false);
      }

      try {
        const transaction = this.DB!.transaction([`${this.storeName}`], 'readwrite');
        const store = transaction.objectStore(this.storeName);

        const request = store.get(key);

        request.onsuccess = () => {
          if (request.result) {
            const _request = store.put(data);

            _request.onsuccess = () => {
              resolve(true);
            };

            _request.onerror = () => {
              resolve(false);
            };
          } else {
            const _request = store.add(data);

            _request.onsuccess = () => {
              resolve(true);
            };

            _request.onerror = () => {
              resolve(false);
            };
          }
        };

        request.onerror = () => {
          resolve(false);
        };
      } catch (err) {
        resolve(false);
      }
    })();
  });

  // eslint-disable-next-line max-len, no-async-promise-executor
  get = (key: string, conditions?: Object) => new Promise<{ isOk: boolean; data: any }>(async (resolve) => {
    LOG?.set({
      logType: 'indexDB-get-start',
    });
    if (isUndefined(this.IDB) || isNull(this.IDB) || this.IDB.readyState !== 'done') {
      const { IDB, DB } = await this.openDB();

      this.IDB = IDB;
      this.DB = DB;

      if (!this.DB) {
        resolve({ isOk: false, data: null });
      }
    }

    try {
      LOG?.set({ logType: 'indexDB-get-try' });

      const transaction = this.DB!.transaction(this.storeName, 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(key);

      let data: object | null = null;

      request.onsuccess = () => {
        LOG?.set({ logType: 'indexDB-get-success' });

        if (request.result) {
          data = request.result;

          if (data && isObject(conditions)) {
            if (isObject(data)) {
              // eslint-disable-next-line no-restricted-syntax
              for (const _key in conditions) {
                if (data && (conditions as any)[_key] !== (data as any)[_key]) {
                  data = null;

                  break;
                }
              }
            }
          }
          resolve({
            isOk: true,
            data,
          });
        } else {
          resolve({
            isOk: false,
            data: null,
          });
        }
      };

      request.onerror = () => {
        resolve({
          isOk: false,
          data: null,
        });
      };
    } catch (err) {
      resolve({
        isOk: false,
        data: null,
      });
    }
  });

  // eslint-disable-next-line no-async-promise-executor
  remove = (key: string) => new Promise<boolean>(async (resolve) => {
    if (isUndefined(this.IDB) || isNull(this.IDB) || this.IDB.readyState !== 'done') {
      const { IDB, DB } = await this.openDB();

      this.IDB = IDB;
      this.DB = DB;

      if (!this.DB) {
        resolve(false);
      }
    }

    try {
      const transaction = this.DB!.transaction(this.storeName, 'readwrite');
      const store = transaction.objectStore(this.storeName);
      store.delete(key);

      resolve(true);
    } catch (err) {
      resolve(false);
    }
  });
}
